/**
 * BuildingScreen
 *
 * Completion screen — shows a brief "building your plan" animation.
 * Fires onSave immediately on mount, then navigates after animation.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { View, Text, Animated, Pressable } from "react-native";
import { EMBER } from "@louise/moon-ui/theme/tokens";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

type Props = {
  onSave: () => Promise<void>;
  onAnimationDone: () => void;
};

export default function BuildingScreen({ onSave, onAnimationDone }: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("BuildingScreen");
  const opacity = useRef(new Animated.Value(0)).current;
  const scale = useRef(new Animated.Value(0.95)).current;
  const progressWidth = useRef(new Animated.Value(0)).current;
  const onAnimationDoneRef = useRef(onAnimationDone);
  const savePromiseRef = useRef<Promise<void> | null>(null);
  const animationFinishedRef = useRef(false);
  const saveSucceededRef = useRef(false);
  const navigatedRef = useRef(false);
  const saveInFlightRef = useRef(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [saveInFlight, setSaveInFlight] = useState(false);
  onAnimationDoneRef.current = onAnimationDone;
  const savedRef = useRef(false);

  const navigateWhenReady = useCallback(() => {
    if (
      !animationFinishedRef.current ||
      !saveSucceededRef.current ||
      navigatedRef.current
    ) {
      return;
    }
    navigatedRef.current = true;
    onAnimationDoneRef.current();
  }, []);

  const attemptSave = useCallback(() => {
    if (saveInFlightRef.current) return;
    saveInFlightRef.current = true;
    setSaveInFlight(true);
    setSaveFailed(false);
    saveSucceededRef.current = false;
    const attempt = Promise.resolve().then(onSave);
    savePromiseRef.current = attempt;
    void attempt
      .then(() => {
        if (savePromiseRef.current !== attempt) return;
        saveSucceededRef.current = true;
        navigateWhenReady();
      })
      .catch(() => {
        if (savePromiseRef.current !== attempt) return;
        setSaveFailed(true);
      })
      .finally(() => {
        if (savePromiseRef.current !== attempt) return;
        saveInFlightRef.current = false;
        setSaveInFlight(false);
      });
  }, [navigateWhenReady, onSave]);

  // Fire save once on mount
  useEffect(() => {
    if (!savedRef.current) {
      savedRef.current = true;
      attemptSave();
    }
  }, [attemptSave]);

  // Run animation and navigate when done
  useEffect(() => {
    Animated.parallel([
      Animated.timing(opacity, {
        toValue: 1,
        duration: 400,
        useNativeDriver: true,
      }),
      Animated.spring(scale, {
        toValue: 1,
        damping: 14,
        stiffness: 150,
        useNativeDriver: true,
      }),
    ]).start();

    Animated.timing(progressWidth, {
      toValue: 1,
      duration: 2200,
      useNativeDriver: false,
    }).start();

    const timer = setTimeout(() => {
      animationFinishedRef.current = true;
      navigateWhenReady();
    }, 2500);

    return () => clearTimeout(timer);
  }, [navigateWhenReady, opacity, scale, progressWidth]);

  return (
    <View
      testID="plan-builder-building-screen"
      style={{
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        padding: 32,
      }}
    >
      <Animated.View
        style={{
          opacity,
          transform: [{ scale }],
          alignItems: "center",
        }}
      >
        {/* Checkmark circle */}
        <View
          style={{
            width: 64,
            height: 64,
            borderRadius: 32,
            backgroundColor: `${EMBER.colors.warm}15`,
            alignItems: "center",
            justifyContent: "center",
            marginBottom: 24,
          }}
        >
          <Text
            style={{
              fontSize: 28,
              color: EMBER.colors.warm,
            }}
          >
            {"\u2713"}
          </Text>
        </View>

        <Text
          className="font-dm-sans"
          style={{
            fontSize: 22,
            fontWeight: "700",
            color: EMBER.colors.text,
            textAlign: "center",
            marginBottom: 8,
          }}
        >
          {t("plan_builder.building.title")}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 15,
            fontWeight: "400",
            color: EMBER.colors.textLight,
            textAlign: "center",
            lineHeight: 22,
            marginBottom: 32,
          }}
        >
          {t("plan_builder.building.subtitle")}
        </Text>

        {/* Progress bar */}
        <View
          style={{
            width: 200,
            height: 4,
            borderRadius: 2,
            backgroundColor: `${EMBER.colors.textFaint}20`,
            overflow: "hidden",
          }}
        >
          <Animated.View
            style={{
              height: 4,
              borderRadius: 2,
              backgroundColor: EMBER.colors.warm,
              width: progressWidth.interpolate({
                inputRange: [0, 1],
                outputRange: ["0%", "100%"],
              }),
            }}
          />
        </View>
        {saveFailed ? (
          <View style={{ alignItems: "center", marginTop: 24 }}>
            <Text
              accessibilityLiveRegion="polite"
              style={{
                color: "#8B3A32",
                fontSize: 14,
                lineHeight: 20,
                textAlign: "center",
              }}
            >
              {t("plan_builder.building.save_error")}
            </Text>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t("plan_builder.building.retry_save")}
              accessibilityState={{ disabled: saveInFlight }}
              disabled={saveInFlight}
              onPress={() => {
                capturePress(
                  pressLabel(t("plan_builder.building.retry_save")),
                  "plan_builder.building.retry_save",
                );
                attemptSave();
              }}
              style={({ pressed }) => ({
                backgroundColor: EMBER.colors.warm,
                borderRadius: 999,
                marginTop: 12,
                opacity: saveInFlight ? 0.5 : pressed ? 0.75 : 1,
                paddingHorizontal: 18,
                paddingVertical: 10,
              })}
            >
              <Text
                style={{
                  color: "#FFFFFF",
                  fontSize: 14,
                  fontWeight: "700",
                }}
              >
                {t("plan_builder.building.retry_save")}
              </Text>
            </Pressable>
          </View>
        ) : null}
      </Animated.View>
    </View>
  );
}
